import React, { useState, useRef } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentArrowDownIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import contactApi from '../../../../repositories/contact';
import { handleError } from '../../../../utils/errorHandler';
import Button from '../../../ui/Button';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface ParsedContact {
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  email: string;
}

interface ValidationResult {
  row: number;
  data: ParsedContact;
  isValid: boolean;
  errors: string[];
}

// Fake/test email patterns to block
const FAKE_EMAIL_PATTERNS = [
  /^test@/i,
  /^example@/i,
  /^fake@/i,
  /^demo@/i,
  /^sample@/i,
  /@test\./i,
  /@example\./i,
  /@fake\./i,
  /@mailinator\./i,
  /@tempmail\./i,
  /@throwaway\./i,
];

const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return { isValid: false, error: 'Invalid email format' };
  }

  for (const pattern of FAKE_EMAIL_PATTERNS) {
    if (pattern.test(email.trim())) {
      return { isValid: false, error: 'Fake/test email not allowed' };
    }
  }

  return { isValid: true };
};

const validateContact = (contact: ParsedContact, row: number): ValidationResult => {
  const errors: string[] = [];

  if (!contact.firstName?.trim()) {
    errors.push('First name is required');
  }
  if (!contact.lastName?.trim()) {
    errors.push('Last name is required');
  }
  if (!contact.company?.trim()) {
    errors.push('Company is required');
  }
  if (!contact.role?.trim()) {
    errors.push('Role is required');
  }

  const emailValidation = validateEmail(contact.email);
  if (!emailValidation.isValid) {
    errors.push(emailValidation.error!);
  }

  return {
    row,
    data: contact,
    isValid: errors.length === 0,
    errors,
  };
};

const parseCSV = (text: string): ParsedContact[] => {
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length < 2) return []; // Need header + at least 1 row

  const contacts: ParsedContact[] = [];

  // Skip header row (index 0)
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));

    if (values.length >= 5) {
      contacts.push({
        firstName: values[0] || '',
        lastName: values[1] || '',
        company: values[2] || '',
        role: values[3] || '',
        email: values[4] || '',
      });
    }
  }

  return contacts;
};

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ isOpen, onClose, onComplete }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validContacts = validationResults.filter(r => r.isValid);
  const invalidContacts = validationResults.filter(r => !r.isValid);

  const resetState = () => {
    setSelectedFile(null);
    setValidationResults([]);
    setShowValidation(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const processFile = async (file: File) => {
    try {
      const text = await file.text();
      const contacts = parseCSV(text);

      if (contacts.length === 0) {
        Swal.fire({
          title: 'Error',
          text: 'No valid data found in CSV. Make sure it has a header row and at least one data row.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      const results = contacts.map((contact, index) => validateContact(contact, index + 2)); // +2 because row 1 is header
      setValidationResults(results);
      setShowValidation(true);
      setSelectedFile(file);
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to read CSV file.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      processFile(file);
    } else {
      Swal.fire({
        title: 'Error',
        text: 'Please select a valid CSV file.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        processFile(file);
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Please select a valid CSV file.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const handleUpload = async () => {
    if (validContacts.length === 0) {
      Swal.fire({
        title: 'Error',
        text: 'No valid contacts to upload.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    try {
      setUploading(true);

      const contactsData = validContacts.map(r => r.data);
      const response = await contactApi.bulkContactUpload(contactsData);

      if (response?.data?.success) {
        const { summary, failed } = response.data;

        if (failed && failed.length > 0) {
          // Some contacts failed on backend
          const failedList = failed
            .slice(0, 10)
            .map((f: { data: ParsedContact; reason: string }) =>
              `${f.data.firstName} ${f.data.lastName} (${f.data.email}): ${f.reason}`
            )
            .join('<br>');

          const moreErrors = failed.length > 10
            ? `<br><br>...and ${failed.length - 10} more`
            : '';

          Swal.fire({
            title: 'Upload Complete',
            html: `
              <p><strong>${summary.created}</strong> contacts added successfully.</p>
              <p><strong>${summary.failed}</strong> contacts failed.</p>
              ${failed.length > 0 ? `
                <hr class="my-4" />
                <p class="text-left text-sm font-semibold">Failed:</p>
                <p class="text-left text-xs text-danger-600">${failedList}${moreErrors}</p>
              ` : ''}
            `,
            icon: summary.created > 0 ? 'warning' : 'error',
            confirmButtonText: 'OK',
          });
        } else {
          // All contacts created successfully
          Swal.fire({
            title: 'Success!',
            text: `${summary.created} contacts added successfully.`,
            icon: 'success',
            confirmButtonText: 'OK',
          });
        }

        resetState();
        onComplete();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = 'First Name,Last Name,Company,Role,Email Address\nJohn,Doe,Spotify,Playlist Curator,john.doe@spotify.com\nJane,Smith,Apple Music,A&R,jane.smith@apple.com';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-black">Bulk Upload Contacts</h3>
          <button onClick={handleClose} className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {!showValidation ? (
            <>
              {/* CSV Format Instructions */}
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-md">
                <p className="text-sm font-semibold text-primary-900 mb-2">CSV Format</p>
                <p className="text-xs text-primary-800 mb-2">
                  Your CSV file should have the following columns:
                </p>
                <ul className="text-xs text-primary-800 list-disc list-inside space-y-1">
                  <li>First Name</li>
                  <li>Last Name</li>
                  <li>Company</li>
                  <li>Role</li>
                  <li>Email Address</li>
                </ul>
                <Button variant="link" size="inline" onClick={downloadSampleCSV} className="mt-3 text-xs">
                  <DocumentArrowDownIcon className="w-4 h-4" />
                  Download Sample CSV
                </Button>
              </div>

              {/* Drag and Drop Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-primary-700 bg-primary-50' : 'border-primary-300'
                }`}
              >
                <ArrowUpTrayIcon className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
                <p className="text-sm text-neutral-600 mb-2">
                  Drag and drop your CSV file here, or
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csvFileInput"
                />
                <label
                  htmlFor="csvFileInput"
                  className="inline-block px-4 py-2 text-sm text-primary-700 border border-primary-700 rounded-md cursor-pointer hover:bg-primary-50"
                >
                  Select File
                </label>
              </div>

              {/* Validation Info */}
              <div className="p-4 bg-warning-50 border border-warning-200 rounded-md">
                <p className="text-xs font-semibold text-warning-900 mb-1">Validation</p>
                <ul className="text-xs text-warning-700 list-disc list-inside space-y-1">
                  <li>All fields are required</li>
                  <li>Email format will be validated</li>
                  <li>Fake emails (test@test.com, example@example.com) will be blocked</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Validation Results */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-success-600">
                    <CheckCircleIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">{validContacts.length} Valid</span>
                  </div>
                  <div className="flex items-center gap-2 text-danger-600">
                    <XCircleIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">{invalidContacts.length} Invalid</span>
                  </div>
                </div>
                <Button variant="link" size="inline" onClick={resetState}>
                  Choose Different File
                </Button>
              </div>

              {/* Invalid Contacts */}
              {invalidContacts.length > 0 && (
                <div className="border border-danger-200 rounded-md overflow-hidden">
                  <div className="bg-danger-50 px-4 py-2 border-b border-danger-200">
                    <p className="text-sm font-semibold text-danger-900">Invalid Rows (will not be uploaded)</p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {invalidContacts.map((result) => (
                      <div key={result.row} className="px-4 py-2 border-b border-danger-100 last:border-b-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-xs font-medium text-neutral-700">Row {result.row}: </span>
                            <span className="text-xs text-neutral-600">
                              {result.data.firstName} {result.data.lastName} - {result.data.email || '(no email)'}
                            </span>
                          </div>
                        </div>
                        <div className="mt-1">
                          {result.errors.map((error, idx) => (
                            <span key={idx} className="text-xs text-danger-600 mr-2">• {error}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Valid Contacts */}
              {validContacts.length > 0 && (
                <div className="border border-success-200 rounded-md overflow-hidden">
                  <div className="bg-success-50 px-4 py-2 border-b border-success-200">
                    <p className="text-sm font-semibold text-success-900">Valid Rows (will be uploaded)</p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {validContacts.map((result) => (
                      <div key={result.row} className="px-4 py-2 border-b border-success-100 last:border-b-0">
                        <span className="text-xs font-medium text-neutral-700">Row {result.row}: </span>
                        <span className="text-xs text-neutral-600">
                          {result.data.firstName} {result.data.lastName} ({result.data.company}) - {result.data.email}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200">
          <Button
            variant="neutral"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          {showValidation && (
            <Button
              onClick={handleUpload}
              disabled={validContacts.length === 0 || uploading}
            >
              {uploading ? 'Uploading...' : `Upload ${validContacts.length} Contacts`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
